/**
 * @module modules/useLambda
 * @description React hooks for calling Netlify serverless functions with support for polling.
 * Provides useLambda for single calls and useLambdaPoll for continuous polling with lifecycle management.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { LambdaHookOptions, LambdaCallArgs, UseLambdaPollParams, UseLambdaPollState } from '../types/hooks';

/**
 * @const LAMBDA_POLL_INTERVAL
 * @description Default polling interval in milliseconds for useLambdaPoll hook
 * @type {number}
 */
export const LAMBDA_POLL_INTERVAL = Number(process.env?.LAMBDA_POLL_INTERVAL || process.env?.REACT_APP_LAMBDA_POLL_INTERVAL) || 5000;

export const LAMBDA_POLL_INTERVAL_INVOICE = Number(process.env?.LAMBDA_POLL_INTERVAL_INVOICE || process.env?.REACT_APP_LAMBDA_POLL_INTERVAL_INVOICE) || 15000;

/**
 * @function getLambdaUrl
 * @description Construct Netlify function URL from function name
 * @param {string} functionName - Name of the Netlify function
 * @returns {string} Full URL path to the Netlify function
 */
export function getLambdaUrl(functionName: string): string {
  return `/.netlify/functions/${functionName}`;
}

/**
 * @function useLambda
 * @description React hook for calling Netlify serverless functions.
 * Provides a memoized callback function for making POST requests to lambda functions.
 * @param {LambdaHookOptions} [options] - Optional hook configuration
 * @returns {{ callLambda: Function }} Object containing the callLambda function
 */
export function useLambda(options?: LambdaHookOptions) {
  /**
   * @function callLambda
   * @template TRequest
   * @template TResponse
   * @async
   * @description Call a Netlify function with type-safe request/response handling
   * @param {LambdaCallArgs<TRequest>} args - Function call arguments including name, payload, and headers
   * @returns {Promise<TResponse>} Promise resolving to the function response
   * @throws {Error} If HTTP response is not ok
   */
  const callLambda = useCallback(
    async <TRequest extends Record<string, unknown>, TResponse = unknown>(
      args: LambdaCallArgs<TRequest>,
    ): Promise<TResponse> => {
      const url = getLambdaUrl(args.functionName);

      const headers = new Headers({ 'Content-Type': 'application/json' });
      if (args.headers) {
        new Headers(args.headers).forEach((value, key) => headers.set(key, value));
      }

      const response = await fetch(url, {
        method: 'POST',
        ...args.requestInit,
        headers,
        body: JSON.stringify(args.payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as TResponse;
    },
    [],
  );

  return { callLambda };
}

/**
 * @function useLambdaPoll
 * @template TRequest
 * @template TResponse
 * @description React hook for continuously polling a Netlify function at regular intervals.
 * Provides start/stop controls, automatic cleanup, and tracks last result/error state.
 * Supports AbortController for canceling in-flight requests.
 * @param {UseLambdaPollParams<TRequest>} params - Polling configuration parameters
 * @param {string} params.functionName - Name of the Netlify function to poll
 * @param {function(): TRequest} params.getPayload - Function that returns the request payload for each poll
 * @param {number} [params.intervalMs=LAMBDA_POLL_INTERVAL] - Polling interval in milliseconds
 * @param {boolean} [params.autoStart=true] - Whether to start polling automatically on mount
 * @param {RequestInit} [params.requestInit] - Additional fetch request options
 * @param {string} [params.baseUrlOverride] - Optional base URL override
 * @param {Record<string, string>} [params.headers] - Additional request headers
 * @returns {UseLambdaPollState<TResponse>} Polling state and control functions
 */
export function useLambdaPoll<
  TRequest extends Record<string, unknown>,
  TResponse = unknown
>({
  functionName,
  getPayload,
  intervalMs = LAMBDA_POLL_INTERVAL,
  autoStart = true,
  requestInit,
  baseUrlOverride,
  headers,
}: UseLambdaPollParams<TRequest>): UseLambdaPollState<TResponse> {
  const { callLambda } = useLambda({ baseUrlOverride });
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<TResponse | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const controllerRef = useRef<AbortController | null>(null);
  const isRunningRef = useRef<boolean>(false);

  /**
   * @function abort
   * @description Abort the current in-flight request if one exists
   */
  const abort = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  /**
   * @function clear
   * @description Clear polling state (result and error) and abort any in-flight request
   */
  const clear = useCallback(() => {
    setLastResult(null);
    setLastError(null);
    abort();
  }, [abort]);

  /**
   * @function tick
   * @async
   * @description Execute a single poll iteration: call the function, update state, schedule next tick.
   * Handles errors gracefully and continues polling if still running.
   * @returns {Promise<void>}
   */
  const tick = useCallback(async (): Promise<void> => {
    try {
      const payload = getPayload();
      // create a fresh controller for this tick
      const controller = new AbortController();
      controllerRef.current = controller;
      const result = await callLambda<TRequest, TResponse>({
        functionName,
        payload,
        headers,
        requestInit: { ...requestInit, signal: controller.signal },
        baseUrlOverride,
      });
      setLastResult(result);
      setLastError(null);
    } catch (err) {
      setLastError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      controllerRef.current = null;
      if (isRunningRef.current) {
        timerRef.current = setTimeout(() => {
          tick();
        }, intervalMs);
      }
    }
  }, [callLambda, functionName, getPayload, requestInit, headers, baseUrlOverride, intervalMs]);

  /**
   * @function start
   * @description Start the polling loop. Does nothing if already running.
   */
  const start = useCallback(() => {
    if (isRunningRef.current) return;
    setIsRunning(true);
    isRunningRef.current = true;
    tick();
  }, [tick]);

  /**
   * @function stop
   * @description Stop the polling loop, clear any pending timers, and abort in-flight requests
   */
  const stop = useCallback(() => {
    if (!isRunningRef.current) return;
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    abort();
    setIsRunning(false);
    isRunningRef.current = false;
  }, [abort]);

  useEffect(() => {
    if (autoStart) {
      start();
    }
    return () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      abort();
      isRunningRef.current = false;
    };
  }, [autoStart, start, abort]);

  return { isRunning, lastResult, lastError, start, stop, clear };
}
